import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [filter, verificationFilter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API}/admin/users`;
      
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('userMode', filter);
      if (verificationFilter !== 'all') params.append('verificationStatus', verificationFilter);
      
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async (userId, suspend) => {
    const reason = suspend ? prompt('Enter reason for suspension:') : '';
    if (suspend && !reason) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/users/${userId}/suspend?suspend=${suspend}&reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${suspend ? 'suspended' : 'unsuspended'} successfully`);
      fetchUsers();
      setShowUserModal(false);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleVerifyUser = async (userId, status) => {
    const notes = prompt(`Enter verification notes (optional):`);
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/users/${userId}/verify?status=${status}&notes=${encodeURIComponent(notes || '')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchUsers();
      setShowUserModal(false);
    } catch (error) {
      console.error('Failed to verify user:', error);
      toast.error('Failed to verify user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user and all their data? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
      setShowUserModal(false);
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Failed to delete user');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      toast.error('Failed to load user details');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Users</option>
                <option value="Agent">Agents</option>
                <option value="Private Owner">Private Owners</option>
                <option value="Influencer">Influencers</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Verification Status</label>
              <select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="flex-1"></div>

            <div className="flex items-end">
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-blue-600">{users.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Verification</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Joined</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{user.fullName}</td>
                    <td className="py-3 px-4 text-gray-600">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.userMode === 'Agent' ? 'bg-blue-100 text-blue-700' :
                        user.userMode === 'Private Owner' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {user.userMode}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.verificationStatus === 'approved' ? 'bg-green-100 text-green-700' :
                        user.verificationStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {user.verificationStatus}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {user.isSuspended ? (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                          Suspended
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => viewUserDetails(user.id)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Detail Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="text-xl font-bold">User Details</h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="font-medium">{selectedUser.fullName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">User Mode</p>
                    <p className="font-medium">{selectedUser.userMode}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Verification Status</p>
                    <p className="font-medium">{selectedUser.verificationStatus}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Listings Count</p>
                    <p className="font-medium">{selectedUser.listingsCount || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Joined Date</p>
                    <p className="font-medium">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>

                {selectedUser.verificationNotes && (
                  <div>
                    <p className="text-sm text-gray-600">Verification Notes</p>
                    <p className="font-medium">{selectedUser.verificationNotes}</p>
                  </div>
                )}

                {/* Agent-Specific Information */}
                {selectedUser.userMode === 'Agent' && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p className="font-semibold mb-3 text-purple-800">Agent Information</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedUser.identityNumber && (
                        <div>
                          <p className="text-gray-600">ID Number</p>
                          <p className="font-medium">{selectedUser.identityNumber}</p>
                        </div>
                      )}
                      {selectedUser.registrationNumber && (
                        <div>
                          <p className="text-gray-600">Registration Number</p>
                          <p className="font-medium">{selectedUser.registrationNumber}</p>
                        </div>
                      )}
                      {selectedUser.agencyName && (
                        <div>
                          <p className="text-gray-600">Agency Name</p>
                          <p className="font-medium">{selectedUser.agencyName}</p>
                        </div>
                      )}
                      {selectedUser.agencyAddress && (
                        <div className="col-span-2">
                          <p className="text-gray-600">Agency Address</p>
                          <p className="font-medium">{selectedUser.agencyAddress}, {selectedUser.city} {selectedUser.postalCode}</p>
                        </div>
                      )}
                    </div>

                    {/* Document Links */}
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <p className="font-semibold mb-2 text-purple-800">Uploaded Documents</p>
                      <div className="flex flex-wrap gap-3">
                        {selectedUser.certificatesUrl && (
                          <a
                            href={selectedUser.certificatesUrl}
                            download="agent-certificate.pdf"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          >
                            📄 Download Certificate
                          </a>
                        )}
                        {selectedUser.idDocumentUrl && (
                          <a
                            href={selectedUser.idDocumentUrl}
                            download="agent-id-document.pdf"
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                          >
                            🪪 Download ID Document
                          </a>
                        )}
                      </div>
                      {!selectedUser.certificatesUrl && !selectedUser.idDocumentUrl && (
                        <p className="text-sm text-gray-500 italic">No documents uploaded yet</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Private Owner-Specific Information */}
                {selectedUser.userMode === 'Private Owner' && (
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="font-semibold mb-3 text-orange-800">Private Owner Verification Documents</p>
                    
                    {selectedUser.identityNumber && (
                      <div className="mb-3 text-sm">
                        <p className="text-gray-600">ID Number</p>
                        <p className="font-medium">{selectedUser.identityNumber}</p>
                      </div>
                    )}

                    {/* Document Links */}
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <p className="font-semibold mb-2 text-orange-800">Uploaded Documents</p>
                      <div className="flex flex-wrap gap-3">
                        {selectedUser.idDocumentUrl && (
                          <a
                            href={selectedUser.idDocumentUrl}
                            download="owner-id-document.pdf"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          >
                            🪪 ID Document
                          </a>
                        )}
                        {selectedUser.proofOfResidencyUrl && (
                          <a
                            href={selectedUser.proofOfResidencyUrl}
                            download="proof-of-residency.pdf"
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                          >
                            🏠 Proof of Residency
                          </a>
                        )}
                        {selectedUser.proofOfAddressUrl && (
                          <a
                            href={selectedUser.proofOfAddressUrl}
                            download="proof-of-address.pdf"
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                          >
                            📍 Proof of Address
                          </a>
                        )}
                      </div>
                      {!selectedUser.idDocumentUrl && !selectedUser.proofOfResidencyUrl && !selectedUser.proofOfAddressUrl && (
                        <p className="text-sm text-gray-500 italic">No documents uploaded yet</p>
                      )}
                    </div>

                    <div className="mt-4 bg-orange-100 p-3 rounded text-sm text-orange-800">
                      <p className="font-semibold mb-1">Required Documents:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>ID (SA ID card/book or passport)</li>
                        <li>Proof of residency/ownership</li>
                        <li>Proof of address (not older than 3 months)</li>
                      </ul>
                    </div>
                  </div>
                )}

                {selectedUser.subscription && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Active Subscription</p>
                    <p className="text-sm">Plan: {selectedUser.subscription.plan}</p>
                    <p className="text-sm">Price: R{selectedUser.subscription.price}</p>
                    <p className="text-sm">Property Limit: {selectedUser.subscription.propertyLimit}</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t bg-gray-50 flex flex-wrap gap-3">
                {selectedUser.verificationStatus === 'pending' && (
                  <>
                    <button
                      onClick={() => handleVerifyUser(selectedUser.id, 'approved')}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      Approve User
                    </button>
                    <button
                      onClick={() => handleVerifyUser(selectedUser.id, 'rejected')}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                      Reject User
                    </button>
                  </>
                )}

                {selectedUser.isSuspended ? (
                  <button
                    onClick={() => handleSuspendUser(selectedUser.id, false)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  >
                    Unsuspend User
                  </button>
                ) : (
                  <button
                    onClick={() => handleSuspendUser(selectedUser.id, true)}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
                  >
                    Suspend User
                  </button>
                )}

                <button
                  onClick={() => handleDeleteUser(selectedUser.id)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  Delete User
                </button>

                <button
                  onClick={() => setShowUserModal(false)}
                  className="ml-auto bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
